/**
 * 실행 환경 판정.
 *
 * PWA 설치 · 웹푸시 가능 여부가 OS/브라우저 조합에 따라 크게 갈리므로,
 * 온보딩 단계를 정하기 전에 환경부터 확정한다.
 *
 * 배경은 이슈 #176 참조. 요약하면:
 * - Android 에서 "진짜 앱" 설치(WebAPK)는 Google Play 서비스가 발급하며,
 *   브라우저가 발급을 요청해야 만들어진다. Edge/Firefox 는 바로가기만 만든다.
 * - iOS 는 Safari 로 홈 화면에 추가한 PWA 에서만 웹푸시가 동작한다.
 *   iOS Chrome 등 다른 브라우저는 설치·푸시 모두 불가.
 */

export type OS = "android" | "ios" | "desktop" | "unknown";

export type Browser = "chrome" | "safari" | "samsung" | "edge" | "firefox" | "inapp" | "other";

export interface Environment {
  os: OS;
  browser: Browser;
  /** 이 OS 에서 PWA 설치·푸시가 온전히 동작하는 브라우저인가 */
  isPreferredBrowser: boolean;
  /** standalone(설치된 앱)으로 실행 중인가 */
  isStandalone: boolean;
  /** 브라우저가 알림 API 자체를 지원하는가 */
  supportsNotification: boolean;
  // HTTPS 또는 localhost. `supportsNotification` 과 따로 두는 이유는 문구다 — 사설 IP
  // 평문에서는 둘 다 false 인데, 그걸 브라우저 탓으로 말하면 크롬 쓰는 사람에게 크롬을
  // 권하게 된다.
  isSecureContext: boolean;
}

/** 카카오톡·네이버 등 인앱 브라우저. 설치 옵션이 없어 별도 취급한다. */
const INAPP_PATTERNS = /kakaotalk|naver|line\/|instagram|fban|fbav|daumapps|everytimeapp/;

function detectBrowser(ua: string): Browser {
  if (INAPP_PATTERNS.test(ua)) return "inapp";
  // Edge(EdgA/Edg) · 삼성(SamsungBrowser) 은 UA 에 Chrome 도 포함하므로 먼저 판정한다.
  if (/edga?\/|edg\//.test(ua)) return "edge";
  if (/samsungbrowser/.test(ua)) return "samsung";
  if (/firefox|fxios/.test(ua)) return "firefox";
  if (/opr\/|opera/.test(ua)) return "other";
  if (/chrome|crios/.test(ua)) return "chrome";
  // iOS 는 모든 브라우저가 WebKit 이라 Safari 판정을 마지막에 둔다.
  if (/safari/.test(ua)) return "safari";
  return "other";
}

function detectOS(ua: string): OS {
  if (/android/.test(ua)) return "android";
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  // iPadOS 는 데스크톱 Safari 로 위장하므로 터치 지점 수로 구분한다.
  if (
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1
  ) {
    return "ios";
  }
  if (/windows|macintosh|linux|cros/.test(ua)) return "desktop";
  return "unknown";
}

/**
 * 해당 OS 에서 PWA 를 온전히 지원하는 브라우저인지.
 *
 * - Android: Chrome · 삼성인터넷 이 WebAPK 를 발급한다.
 * - iOS: Safari 만 홈 화면 추가·웹푸시가 가능하다.
 * - 데스크톱: Chromium 계열(Chrome/Edge) 모두 앱 창 설치가 정상 동작하므로 구분하지 않는다.
 */
function isPreferred(os: OS, browser: Browser): boolean {
  if (browser === "inapp") return false;
  switch (os) {
    case "android":
      return browser === "chrome" || browser === "samsung";
    case "ios":
      return browser === "safari";
    case "desktop":
      return browser === "chrome" || browser === "edge";
    default:
      return false;
  }
}

export function detectEnvironment(): Environment {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      os: "unknown",
      browser: "other",
      isPreferredBrowser: false,
      isStandalone: false,
      supportsNotification: false,
      isSecureContext: false,
    };
  }

  const ua = navigator.userAgent.toLowerCase();
  const os = detectOS(ua);
  const browser = detectBrowser(ua);

  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari 의 비표준 standalone 플래그
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  return {
    os,
    browser,
    isPreferredBrowser: isPreferred(os, browser),
    isStandalone,
    supportsNotification: "Notification" in window,
    isSecureContext: window.isSecureContext === true,
  };
}

/**
 * 현재 URL 을 Chrome 으로 여는 Android intent URL.
 *
 * fallback 이 없으면 Chrome 미설치 시 아무 일도 일어나지 않으므로 반드시 함께 넘긴다.
 * iOS 는 이 스킴을 인식하지 못한다 — Android 에서만 사용할 것.
 */
/**
 * 수동 설치 안내 문구. OS 마다 경로가 달라 한 문장으로 못 쓴다.
 *
 * "이미 설치하셨다면" 을 덧붙이는 이유 — 설치된 앱을 브라우저 탭으로 열어도
 * `beforeinstallprompt` 는 오지 않는다. 그 경우와 Chrome 이 이벤트를 억제한 경우를
 * 구분할 표준 API 가 없으므로 문구를 중립적으로 둔다.
 */
export function installGuideText(os: OS): string {
  if (os === "ios") {
    return "하단 공유 버튼 → 홈 화면에 추가를 누른 뒤 홈 아이콘으로 열어주세요. iPhone은 설치해야 알림을 받을 수 있습니다.";
  }
  if (os === "desktop") {
    return "주소창 오른쪽의 설치 아이콘 또는 브라우저 메뉴에서 '앱 설치'를 선택하세요. 이미 설치하셨다면 설치된 앱으로 열어주세요.";
  }
  return "브라우저 메뉴에서 '앱 설치' 또는 '홈 화면에 추가'를 선택하세요. 이미 설치하셨다면 홈 아이콘으로 열어주세요.";
}

/**
 * 알림을 한 번 거부한 뒤 되돌리는 경로.
 *
 * 이 안내가 필요한 이유 — `Notification.requestPermission()` 은 거부된 상태에서
 * 프롬프트 없이 즉시 `denied` 로 끝난다. 앱에서 다시 물어볼 방법이 없고, 브라우저나
 * OS 설정으로 직접 보내는 표준 API 도 없다. 남은 건 "어디를 눌러라"를 말해 주는 것뿐이다.
 *
 * 메뉴 이름은 버전에 따라 갈린다(Chrome 은 `권한`·`사이트 설정` 이 섞인다). 한 낱말에
 * 기대지 않도록 두 표기를 같이 적는다.
 */
export function notificationGuideText(os: OS, browser: Browser): string {
  if (os === "ios") {
    return "iPhone 설정 앱 → 알림 → Safers 에서 '알림 허용'을 켜주세요.";
  }
  if (os === "android") {
    if (browser === "samsung") {
      return "주소창 왼쪽 자물쇠 → 권한 → 알림을 '허용'으로 바꾼 뒤 새로고침해주세요.";
    }
    return "주소창 왼쪽 자물쇠(또는 ⓘ) → 권한 · 사이트 설정 → 알림을 '허용'으로 바꾼 뒤 새로고침해주세요.";
  }
  if (os === "desktop") {
    return "주소창 왼쪽 자물쇠 → 사이트 설정 → 알림을 '허용'으로 바꾼 뒤 새로고침해주세요.";
  }
  return "브라우저의 사이트 설정에서 이 사이트의 알림을 '허용'으로 바꾼 뒤 새로고침해주세요.";
}

export function buildChromeIntentUrl(url: string = window.location.href): string {
  const parsed = new URL(url);
  const withoutScheme = `${parsed.host}${parsed.pathname}${parsed.search}`;
  const fallback = encodeURIComponent(url);
  return `intent://${withoutScheme}#Intent;scheme=${parsed.protocol.replace(":", "")};package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
}
